import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { 
  Map, 
  ArrowRight, 
  Loader2, 
  CheckCircle, 
  Star,
  Clock,
  DollarSign,
  TrendingUp,
  Lightbulb,
  Download,
  Target,
  Home,
  Brain,
  Zap
} from 'lucide-react'
import { useJourneyStore } from '@/stores/journeyStore'

interface AITool {
  id: string
  name: string
  category: 'automation' | 'analytics' | 'content' | 'communication'
  description: string
  difficulty: 'easy' | 'medium' | 'hard'
  timeToImplement: string
  monthlyCost: number
  roi: 'low' | 'medium' | 'high' | 'very high'
  priority: number
  matchScore: number
}

interface AssessmentQuestion {
  id: string
  question: string
  options: Array<{
    value: string
    label: string
    weight: number
  }>
}

interface Roadmap {
  phase1: AITool[]
  phase2: AITool[]
  phase3: AITool[]
  totalCost: number
  estimatedROI: string
  timeframe: string
}

export function Stage5Recommendations() {
  const navigate = useNavigate()
  const { user, stages, completeStage, updateStageProgress } = useJourneyStore()
  const [isCompleting, setIsCompleting] = useState(false)
  const [completionMessage, setCompletionMessage] = useState('')
  const [currentStep, setCurrentStep] = useState<'assessment' | 'generating' | 'results'>('assessment')
  const [assessmentComplete, setAssessmentComplete] = useState(false)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [personalizedRoadmap, setPersonalizedRoadmap] = useState<Roadmap | null>(null)
  const [questionsAnswered, setQuestionsAnswered] = useState(0)

  const assessmentQuestions: AssessmentQuestion[] = [
    {
      id: 'primary_goal',
      question: 'What is your primary goal for implementing AI in your business?',
      options: [
        { value: 'efficiency', label: 'Increase operational efficiency', weight: 1.0 },
        { value: 'cost_reduction', label: 'Reduce operational costs', weight: 0.8 },
        { value: 'customer_service', label: 'Improve customer service', weight: 0.9 },
        { value: 'growth', label: 'Accelerate business growth', weight: 1.2 },
      ]
    },
    {
      id: 'biggest_challenge',
      question: 'What is your biggest operational challenge right now?',
      options: [
        { value: 'repetitive_tasks', label: 'Too many repetitive tasks', weight: 1.1 },
        { value: 'data_analysis', label: 'Difficulty analyzing data', weight: 0.9 },
        { value: 'customer_response', label: 'Slow customer response times', weight: 1.0 },
        { value: 'marketing', label: 'Creating consistent marketing content', weight: 0.8 },
      ]
    },
    {
      id: 'tech_comfort',
      question: 'How comfortable are you with new technology?',
      options: [
        { value: 'very_comfortable', label: 'Very comfortable - I enjoy learning new tools', weight: 1.2 },
        { value: 'comfortable', label: 'Comfortable - I can figure things out', weight: 1.0 },
        { value: 'somewhat', label: 'Somewhat - I prefer simple solutions', weight: 0.7 },
        { value: 'not_comfortable', label: 'Not comfortable - I need lots of support', weight: 0.5 },
      ]
    },
    {
      id: 'budget_range',
      question: 'What is your monthly budget for AI tools?',
      options: [
        { value: 'under_100', label: 'Under $100/month', weight: 0.5 },
        { value: '100_500', label: '$100-500/month', weight: 0.8 },
        { value: '500_1000', label: '$500-1000/month', weight: 1.0 },
        { value: 'over_1000', label: 'Over $1000/month', weight: 1.3 },
      ]
    },
    {
      id: 'implementation_speed',
      question: 'How quickly do you want to see results?',
      options: [
        { value: 'immediate', label: 'Immediately - within 1 week', weight: 0.8 },
        { value: 'month', label: 'Within 1 month', weight: 1.0 },
        { value: 'quarter', label: 'Within 3 months', weight: 1.1 },
        { value: 'patient', label: 'I\'m patient - within 6+ months', weight: 1.3 },
      ]
    }
  ]

  const aiToolDatabase: AITool[] = [
    {
      id: 'chatgpt-plus',
      name: 'ChatGPT Plus',
      category: 'content',
      description: 'AI assistant for content creation, customer service, and brainstorming',
      difficulty: 'easy',
      timeToImplement: '1 day',
      monthlyCost: 20,
      roi: 'high',
      priority: 1,
      matchScore: 0
    },
    {
      id: 'zapier',
      name: 'Zapier',
      category: 'automation',
      description: 'Connect apps and automate workflows without coding',
      difficulty: 'medium',
      timeToImplement: '1 week',
      monthlyCost: 49,
      roi: 'very high',
      priority: 2,
      matchScore: 0
    },
    {
      id: 'notion-ai',
      name: 'Notion AI',
      category: 'content',
      description: 'AI-powered knowledge management and documentation',
      difficulty: 'easy',
      timeToImplement: '3 days',
      monthlyCost: 10,
      roi: 'medium',
      priority: 3,
      matchScore: 0
    },
    {
      id: 'calendly',
      name: 'Calendly',
      category: 'automation',
      description: 'Automated scheduling and calendar management',
      difficulty: 'easy',
      timeToImplement: '2 hours',
      monthlyCost: 12,
      roi: 'high',
      priority: 4,
      matchScore: 0
    },
    {
      id: 'hubspot',
      name: 'HubSpot CRM',
      category: 'communication',
      description: 'AI-powered customer relationship management',
      difficulty: 'medium',
      timeToImplement: '2 weeks',
      monthlyCost: 50,
      roi: 'very high',
      priority: 5,
      matchScore: 0
    },
    {
      id: 'tableau',
      name: 'Tableau',
      category: 'analytics',
      description: 'Advanced data visualization and business intelligence',
      difficulty: 'hard',
      timeToImplement: '1 month',
      monthlyCost: 70,
      roi: 'high',
      priority: 6,
      matchScore: 0
    }
  ]

  const generatePersonalizedRoadmap = async () => {
    setCurrentStep('generating')
    
    // Simulate AI analysis
    await new Promise(resolve => setTimeout(resolve, 3000))
    
    // Calculate tool match scores based on answers and user profile
    const scoredTools = aiToolDatabase.map(tool => {
      let score = 0.5 // Base score
      
      // Adjust based on user answers
      if (answers.primary_goal === 'efficiency' && tool.category === 'automation') score += 0.3
      if (answers.biggest_challenge === 'repetitive_tasks' && tool.category === 'automation') score += 0.4
      if (answers.tech_comfort === 'very_comfortable') score += 0.1
      if (answers.budget_range === 'over_1000' || tool.monthlyCost < 50) score += 0.2
      
      // Adjust based on business type
      if (user?.businessType?.includes('Small') && tool.monthlyCost < 30) score += 0.2
      
      // Adjust based on AI experience
      if (user?.currentAIExperience === 'intermediate' && tool.difficulty === 'medium') score += 0.2
      if (user?.currentAIExperience === 'basic' && tool.difficulty === 'easy') score += 0.3
      
      return { ...tool, matchScore: Math.min(1, score) }
    })
    
    // Sort by match score and organize into phases
    const sortedTools = scoredTools.sort((a, b) => b.matchScore - a.matchScore)
    
    const roadmap: Roadmap = {
      phase1: sortedTools.slice(0, 2), // Top 2 tools for immediate implementation
      phase2: sortedTools.slice(2, 4), // Next 2 tools for month 2-3
      phase3: sortedTools.slice(4, 6), // Advanced tools for later
      totalCost: sortedTools.slice(0, 4).reduce((sum, tool) => sum + tool.monthlyCost, 0),
      estimatedROI: '300-500%',
      timeframe: '6 months'
    }
    
    setPersonalizedRoadmap(roadmap)
    setCurrentStep('results')
    
    updateStageProgress(5, {
      interactionData: {
        questionsAnswered,
        assessmentComplete: true,
        roadmapGenerated: true
      }
    })
  }

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }))
    if (!answers[questionId]) {
      setQuestionsAnswered(prev => prev + 1)
    }
  }

  const handleStartAssessment = () => {
    setAssessmentComplete(true)
    generatePersonalizedRoadmap()
  }

  const handleDownloadRoadmap = () => {
    const roadmapData = {
      user: user?.name,
      businessType: user?.businessType,
      industry: user?.industry,
      roadmap: personalizedRoadmap,
      generatedAt: new Date().toISOString()
    }
    
    const blob = new Blob([JSON.stringify(roadmapData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'ai-implementation-roadmap.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleComplete = async () => {
    if (isCompleting) return
    
    setIsCompleting(true)
    setCompletionMessage('Completing your AI journey...')
    
    try {
      const stageData = {
        questionsAnswered,
        assessmentComplete,
        roadmapGenerated: !!personalizedRoadmap,
        completed: true
      }
      
      completeStage(5, stageData)
      setCompletionMessage('🎉 Congratulations! Your AI journey is complete!')
      
      // Store will handle navigation to dashboard for final stage
      // No immediate navigation needed
    } catch (error) {
      console.error('Error completing stage:', error)
      setCompletionMessage('Error completing stage. Please try again.')
      setIsCompleting(false)
    }
  }

  const allQuestionsAnswered = assessmentQuestions.every(q => answers[q.id])

  const renderPhaseCard = (phase: AITool[], phaseNumber: number, title: string, timeframe: string) => (
    <Card key={phaseNumber}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold">
            {phaseNumber}
          </div>
          {title}
        </CardTitle>
        <CardDescription>{timeframe}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {phase.map((tool) => (
            <div key={tool.id} className="border rounded-lg p-3">
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-medium">{tool.name}</h4>
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm">{Math.round(tool.matchScore * 100)}%</span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-2">{tool.description}</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {tool.timeToImplement}
                </div>
                <div className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  ${tool.monthlyCost}/mo
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Stage Header */}
      <div className="text-center space-y-4">
        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
          <Map className="w-8 h-8 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Stage 5: Personalized AI Roadmap</h1>
          <p className="text-lg text-muted-foreground">Get your custom AI implementation strategy</p>
        </div>
      </div>

      {/* Assessment Phase */}
      {currentStep === 'assessment' && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-600" />
                  Business Assessment
                </CardTitle>
                <CardDescription>
                  Help us understand your specific needs to create a personalized roadmap
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {assessmentQuestions.map((question, index) => (
                  <div key={question.id}>
                    <h3 className="font-medium mb-3">{index + 1}. {question.question}</h3>
                    <div className="space-y-2">
                      {question.options.map((option) => (
                        <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                          <input
                            type="radio"
                            name={question.id}
                            value={option.value}
                            checked={answers[question.id] === option.value}
                            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                            className="form-radio"
                          />
                          <span className="text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                
                <div className="pt-4">
                  <Button 
                    onClick={handleStartAssessment}
                    disabled={!allQuestionsAnswered}
                    className="w-full gap-2"
                  >
                    Generate My Roadmap <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Assessment Progress</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Questions answered:</span>
                  <span className="font-medium">{questionsAnswered}/{assessmentQuestions.length}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${(questionsAnswered / assessmentQuestions.length) * 100}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Your Profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Business:</span>
                  <span className="font-medium">{user?.businessType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Industry:</span>
                  <span className="font-medium">{user?.industry}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">AI Experience:</span>
                  <span className="font-medium capitalize">{user?.currentAIExperience}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Generating Phase */}
      {currentStep === 'generating' && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
              <h3 className="text-xl font-semibold">Analyzing Your Needs...</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Our AI is processing your responses, business profile, and journey data to create 
                a personalized implementation roadmap just for you.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Phase */}
      {currentStep === 'results' && personalizedRoadmap && (
        <div className="space-y-6">
          {/* Summary */}
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Your AI Implementation Roadmap
              </CardTitle>
              <CardDescription>
                Customized for {user?.businessType} in {user?.industry}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-primary">{personalizedRoadmap.timeframe}</div>
                  <div className="text-sm text-muted-foreground">Implementation Timeline</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">${personalizedRoadmap.totalCost}</div>
                  <div className="text-sm text-muted-foreground">Monthly Investment</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">{personalizedRoadmap.estimatedROI}</div>
                  <div className="text-sm text-muted-foreground">Estimated ROI</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Implementation Phases */}
          <div className="grid lg:grid-cols-3 gap-6">
            {renderPhaseCard(personalizedRoadmap.phase1, 1, "Quick Wins", "Month 1")}
            {renderPhaseCard(personalizedRoadmap.phase2, 2, "Build Momentum", "Months 2-3")}
            {renderPhaseCard(personalizedRoadmap.phase3, 3, "Advanced Tools", "Months 4-6")}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button onClick={handleDownloadRoadmap} variant="outline" className="gap-2">
              <Download className="w-4 h-4" />
              Download Roadmap
            </Button>
            
            {completionMessage && (
              <div className="text-center p-2 bg-primary/10 text-primary rounded-md text-sm">
                {completionMessage}
              </div>
            )}
            
            <Button 
              onClick={handleComplete} 
              disabled={isCompleting}
              className="gap-2"
            >
              {isCompleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>Complete Journey <ArrowRight className="w-4 h-4" /></>
              )}
            </Button>
            
            <Button 
              onClick={() => navigate('/')} 
              variant="ghost" 
              className="gap-2"
            >
              <Home className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}